import { ReactElement, useEffect, useMemo, useState } from "react";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { Checkbox, message, Popconfirm, Select, Tooltip } from "antd5";
import { Flex } from "styles/utility-components";

import { ButtonLink } from "components/actions/Links";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { OPPORTUNITY_CRM, useVariableValue } from "lib/featureFlags";
import {
  DescribeObjectResponse,
  DescribeOpportunityFieldsResponse,
  OpportunityConfigBuilderRequest,
  OpportunityConfigBuilderResponseStatusEnum,
  ProviderTypes,
} from "lib/generated/integration-api";
import { useDescribeEntity } from "lib/hooks/api/integrations/useDescribeEntity";
import { useViewOpportunityFieldsProvider } from "lib/hooks/api/integrations/useDescribeOpportunityFieldsProvider";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useListOpportunityEntitiesProvider } from "lib/hooks/api/integrations/useListOpportunityEntitiesProvider";
import { useUpdateOpportunityConfig } from "lib/hooks/api/integrations/useUpdateOpportunityConfig";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes, settingsRoutes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { getIntegrationIcon, getProviderMetadata } from "./util";

import css from "./IntegrationSetup.module.scss";

type FormValue = {
  id: string;
  name: string;
  required: boolean;
  hasMarkedRequired: boolean;
};

const providerEntityToLabel: Record<ProviderTypes, Record<string, string>> = {
  SALESFORCE: {
    Opportunity: "Opportunities",
    Lead: "Leads",
  },
  HUBSPOT: { Deal: "Deals" },
};

export function IntegrationSetup() {
  const [selectedEntity, setSelectedEntity] = useState<string>("");

  const { data: providerTypeResponse, isLoading: isLoadingProvider } = useFindIntegrationProvider();
  const providerType = providerTypeResponse?.providerType;

  const { data: opportunityEntities, isInitialLoading: isLoadingOpportunityEntities } =
    useListOpportunityEntitiesProvider({ enabled: !!providerType });

  const { data: opportunityFields, isInitialLoading: isLoadingOpportunityFields } =
    useViewOpportunityFieldsProvider({
      enabled: !!providerType,
    });

  const { data: entityDetails, isInitialLoading: isLoadingEntityDetails } = useDescribeEntity(
    selectedEntity,
    {
      enabled: selectedEntity.length !== 0 && !!providerType,
    },
  );

  useEffect(() => {
    if (opportunityEntities && opportunityEntities.entities.length > 0) {
      setSelectedEntity(opportunityEntities.entities[0]);
    }
  }, [opportunityEntities, setSelectedEntity]);

  if (
    isLoadingProvider ||
    isLoadingOpportunityEntities ||
    isLoadingOpportunityFields ||
    isLoadingEntityDetails
  ) {
    return (
      <Wrapper>
        <CentredSpinner />
      </Wrapper>
    );
  }

  if (!providerType) {
    return (
      <Wrapper>
        <EmptyState
          size="large"
          heading="No CRM connected"
          description="To customise your integration, please connect to your CRM provider first"
          type="loadError"
        />
      </Wrapper>
    );
  }

  if (!selectedEntity || !opportunityFields || !entityDetails) {
    return (
      <Wrapper>
        <EmptyState
          size="large"
          heading="No integration settings found"
          description="Unable to get integration settings. Please contact us if the problem persists"
          type="loadError"
        />
      </Wrapper>
    );
  }

  return (
    <IntegrationSetupContent
      providerType={providerType}
      opportunityFields={opportunityFields}
      entityDetails={entityDetails}
      selectedEntity={selectedEntity}
    />
  );
}

function IntegrationSetupContent({
  providerType,
  opportunityFields,
  entityDetails,
  selectedEntity,
}: {
  providerType: ProviderTypes;
  opportunityFields: DescribeOpportunityFieldsResponse;
  entityDetails: DescribeObjectResponse;
  selectedEntity: string;
}) {
  const isOpportunityCrmEnabled = useVariableValue(OPPORTUNITY_CRM, false);

  const [addFieldVisible, setAddFieldVisible] = useState<boolean>(true);
  const [formState, setFormState] = useState<Record<string, FormValue>>({});
  const { logEvent } = useTracking();

  const { mutate: updateOpportunityConfig, isLoading } = useUpdateOpportunityConfig({
    onSuccess: (response) => {
      if (response.status === OpportunityConfigBuilderResponseStatusEnum.Failure) {
        void message.error(response.message ?? "Error saving settings");
        return;
      }

      logEvent(EventNames.settingsSavedForIntegration, {
        "Page source": "Integration Configuration",
        "Connected provider": providerType,
      });

      void message.success(`${providerName} settings successfully saved`);
    },
  });

  useEffect(() => {
    const fields: Record<string, FormValue> = {};

    if (entityDetails && opportunityFields) {
      for (const field of opportunityFields.fields) {
        const fieldData = entityDetails.entity.fields.find((x) => x.id === field.id);
        if (fieldData) {
          fields[fieldData.id] = {
            required: fieldData.required,
            id: field.id,
            name: fieldData.name,
            hasMarkedRequired: field.required,
          };
        }
      }
    }

    setFormState(fields);
  }, [entityDetails, opportunityFields, setFormState]);

  const remainingFields = useMemo(() => {
    return entityDetails?.entity.fields.filter((x) => !formState[x.id]) || [];
  }, [entityDetails, formState]);

  const providerName = getProviderMetadata(providerType, isOpportunityCrmEnabled).name;

  const handleFieldRequired = (id: string, checked: boolean) => {
    setFormState((oldState) => ({
      ...oldState,
      [id]: {
        ...oldState[id],
        hasMarkedRequired: checked,
      },
    }));
    const event = checked
      ? EventNames.fieldMarkedRequiredForIntegration
      : EventNames.fieldMarkedNotRequiredForIntegration;
    logEvent(event, {
      "Page source": "Integration Configuration",
      "Connected provider": providerType,
      "Field id": id,
      "Stotles object": entityDetails.entity.name,
    });
  };

  const handleFieldDelete = (id: string) => {
    setFormState((oldState) => {
      const newState = { ...oldState };
      delete newState[id];
      return newState;
    });

    logEvent(EventNames.fieldRemovedForIntegration, {
      "Page source": "Integration Configuration",
      "Connected provider": providerType,
      "Field id": id,
      "Stotles object": entityDetails.entity.name,
    });
  };

  const handleAddField = (value: ReactElement) => {
    if (typeof value === "string") {
      const entityField = entityDetails.entity.fields.find((x) => x.id === value);
      if (entityField) {
        setFormState((current) => ({
          ...current,
          [entityField.id]: {
            required: entityField.required,
            id: entityField.id,
            name: entityField.name,
            hasMarkedRequired: entityField.required,
          },
        }));
        setAddFieldVisible(true);
      }

      logEvent(EventNames.fieldAddedForIntegration, {
        "Page source": "Integration Configuration",
        "Connected provider": providerType,
        "Field id": value,
        "Stotles object": entityDetails.entity.name,
      });
    }
  };

  const handleSave = () => {
    const request: OpportunityConfigBuilderRequest = {
      entityName: selectedEntity,
      fields: [],
    };

    for (const [key, value] of Object.entries(formState)) {
      let stotlesField = "";

      // temp work around TODO: this needs to move to the backend
      if (key === "dealname" || key === "Name" || key === "Surname") {
        stotlesField = "title";
      }
      if (key === "hubspot_owner_id" || key === "OwnerId") {
        stotlesField = "owner";
      }
      if (key === "accountId" || key === "AccountId") {
        stotlesField = "account";
      }
      if (key === "amount" || key === "Amount") {
        stotlesField = "value";
      }
      if (key === "description" || key === "Description") {
        stotlesField = "description";
      }
      if (key === "closedate" || key === "CloseDate") {
        stotlesField = "closeDate";
      }

      request.fields.push({
        name: key,
        isRequired: value.hasMarkedRequired,
        stotlesField,
      });
    }

    updateOpportunityConfig({ request });
  };

  return (
    <div className={css.setupPage}>
      <div className={css.integrationLogos}>
        <div>{getIntegrationIcon(providerType, 50)}</div>
        <UIcon icon="stotles" size={50} ariaLabel="Stotles logo" />
      </div>
      <h2 className={css.title}>Customise your {providerName} Integration</h2>
      <p>Select the the {providerName} fields you would like to push to from Stotles.</p>
      <Flex justifyContent="center" className={css.navigation} gap="8px">
        <ButtonLink
          to={settingsRoutes.integrations}
          className={css.backButton}
          eventName={EventNames.backButtonFromIntegrationClicked}
          eventAttributes={{
            "Page source": "Integration Configuration",
            "Connected provider": providerType,
          }}
        >
          <ArrowLeftOutlined /> Back
        </ButtonLink>
        <ButtonLink
          to={isOpportunityCrmEnabled ? routes.opportunities : routes.notices}
          eventName={EventNames.viewFeedFromIntegrationClicked}
          eventAttributes={{
            "Page source": "Integration Configuration",
            "Connected provider": providerType,
          }}
        >
          Go to {isOpportunityCrmEnabled ? "Opportunities" : "your feed"}
        </ButtonLink>
      </Flex>
      <div className={css.setupForm}>
        <div className={css.setupFormHeader}>
          <Flex className={css.heading} justifyContent="center" alignItems="center" gap={4}>
            When pushing to <IconWrapper>{getIntegrationIcon(providerType)}</IconWrapper>
            <b>{providerEntityToLabel[providerType]?.[selectedEntity] || selectedEntity}</b>
            from
            <IconWrapper>
              <UIcon icon="stotles" size={16} />
            </IconWrapper>
            <b>{isOpportunityCrmEnabled ? "Opportunities" : "Procurement notices"}</b>
          </Flex>
          <p>
            Choose which {providerName} fields you want to fill in when pushing{" "}
            {isOpportunityCrmEnabled ? "Opportunities" : "Procurement notices"} to your{" "}
            {providerEntityToLabel[providerType]?.[selectedEntity] || selectedEntity}
          </p>
        </div>
        <div className={css.setupBody}>
          <table className={css.fieldTable}>
            <tbody>
              <tr className={css.fieldHeading}>
                <th>{providerName}</th>
                <th>
                  Required{" "}
                  <Tooltip title="Select which fields you want to be required before pushing to your CRM from Stotles.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </th>
              </tr>
              {Object.entries(formState).map(([fieldId, formValue]) => (
                <tr key={fieldId}>
                  <td>{formValue.name}</td>
                  <td>
                    <Checkbox
                      checked={formValue.hasMarkedRequired}
                      disabled={formValue.required}
                      onChange={(e) => {
                        handleFieldRequired(formValue.id, e.target.checked);
                      }}
                    />
                  </td>
                  <td>
                    {!formValue.required && (
                      <Popconfirm
                        title={`Are you sure you want to delete this field?`}
                        okText="Ok"
                        cancelText="Cancel"
                        onConfirm={() => {
                          handleFieldDelete(formValue.id);
                        }}
                      >
                        <DeleteOutlined className={css.removeField} />
                      </Popconfirm>
                    )}
                  </td>
                </tr>
              ))}
              {remainingFields.length > 0 && (
                <tr>
                  <td>
                    <Select
                      showSearch={true}
                      placeholder={
                        <>
                          <PlusOutlined /> Add Fields
                        </>
                      }
                      value={
                        <>
                          <PlusOutlined /> Add Fields
                        </>
                      }
                      onSelect={(value: ReactElement) => handleAddField(value)}
                      className={css.fieldSelect}
                      showArrow={false}
                      options={remainingFields?.map((x) => ({
                        value: entityDetails.entity.fields.find((f) => f.id === x.id)?.id,
                        label: x.name,
                      }))}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={css.setupFooter}>
          <Button onClick={handleSave} disabled={!addFieldVisible} loading={isLoading}>
            Save settings
          </Button>
        </div>
      </div>
    </div>
  );
}

const IconWrapper = styled(Flex)(({ theme }) => ({
  padding: "4px",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: "4px",
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const Wrapper = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
}));
